import {Injectable} from '@angular/core';
import * as MJ from 'jalali-moment';
import {ServerTimeService} from '@shared/services/server-time.service';


@Injectable({
  providedIn: 'root'
})
export class UtilityService {

  constructor(private serverTimeService: ServerTimeService) {
  }


  preventAnyCharactersExceptDigits(input: string) {
    const numbers = ['۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹', '۰', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩', '٠', '1', '2', '3', '4', '5', '6', '7', '8', '9', '0'];
    const filteredString = input.split('').filter(char => numbers.includes(char)).join('');
    return this.convertPersianToEnglish(filteredString);
  }


  preventAlphabet(input: string): boolean {
    const numbers = ['۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹', '۰', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩', '٠', '1', '2', '3', '4', '5', '6', '7', '8', '9', '0'];
    return input.split('').every(char => numbers.includes(char));
  }

  convertPersianToEnglish(strNum: string): string {
    const persianNumbers = ['۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹', '۰'];
    const arabicNumbers = ['١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩', '٠'];
    const englishNumbers = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'];

    return strNum.split('').map(c => englishNumbers[persianNumbers.indexOf(c)] || englishNumbers[arabicNumbers.indexOf(c)] || c).join('');
  }

  numericalValidationMultiLang(strNum: string): string {
    const isValid = this.preventAlphabet(strNum);
    if (!isValid) return strNum.substring(0, strNum.length - 1);

    const result = this.convertPersianToEnglish(strNum);
    return strNum !== result ? result : strNum;
  }

  convertStringToDateObject(expression: any): { day: number, month: number, year: number } {
    expression = isNaN(expression) ? expression : String(expression);
    return {
      day: expression.slice(6, 8),
      month: expression.slice(4, 6),
      year: expression.slice(0, 4)
    };
  }

  convertStringToTimeObject(expression: any): { hour: number, minute: number, second: number } {
    expression = isNaN(expression) ? expression : String(expression);
    return {
      hour: expression.slice(8, 10),
      minute: expression.slice(10, 12),
      second: expression.slice(12, 14) ? expression.slice(12, 14) : '00'
    };
  }

  convertStingToDate(strNum: any) {
    if (!strNum) {
      return false
    } else {
      let minDate = strNum?.toString()
      if (minDate.length === 8) {
        return minDate.slice(0, 4) + '/' + minDate.slice(4, 6) + '/' + minDate.slice(6, 8);
      } else {
        return minDate.slice(0, 4) + '/' + minDate.slice(4, 6) + '/' + minDate.slice(6, 8) + ' ' + minDate.slice(8, 10) + ':' + minDate.slice(10, 12) + '00';
      }
    }
  }


  clone(obj: any): any {
    let copy;
    // Handle the 3 simple types, and null or undefined
    if (null == obj || 'object' != typeof obj) {
      return obj;
    }
    // Handle Date
    if (obj instanceof Date) {
      copy = new Date();
      copy.setTime(obj.getTime());
      return copy;
    }
    // Handle Array
    if (obj instanceof Array) {
      copy = [];
      for (let i = 0, len = obj.length; i < len; i++) {
        copy[i] = this.clone(obj[i]);
      }
      return copy;
    }
    // Handle Object
    if (obj instanceof Object) {
      let copy: any = {}
      for (const attr in obj) {
        if (obj.hasOwnProperty(attr)) {
          copy[attr] = this.clone(obj[attr]);
        }
      }
      return copy;
    }
    throw new Error('Unable to copy obj! Its type isn\'t supported.');
  }

  containsNonLatinCodepoints(s: any): boolean {
    s = s.replace(/(\r\n|\n|\r)/gm, '');
    return /[^A-Za-z0-9 \\r\\n@£$¥èéùìòÇØøÅå\u0394_\u03A6\u0393\u0027\u0022\u039B\u03A9\u03A0\u03A8\u03A3\u0398\u039EÆæßÉ!\#$%&amp;()*+,\\./\-:;&lt;=&gt;?¡ÄÖÑÜ§¿äöñüà^{}\\\\\\[~\\]|\u20AC]*/.test(s);
  }


  convertJalaliToDate(jalaliDate: string): Date {
    // Parse the Jalali date from the string
    return MJ.from(jalaliDate, 'fa', 'YYYYMMDDHHmmss').locale('en').toDate()
    // const date = MJ(jalaliDate, 'YYYYMMDDHHmmss', 'en' ).toDate().getTime();
    // Convert to a timestamp
    // return date
  }
  /** Example: "36:00:00" → 129600000 ms (36h) */
  parseDurationToMs(hhmmss: string ): number {
    if (!hhmmss) return 0;
    const [h, m, s] = hhmmss.split(':').map(Number);
    return (h * 3600 + m * 60 + s) * 1000;
  }

  getToday() {
    return MJ(this.serverTimeService.getNow()).locale('fa').format('jYYYYjMMjDD');
  }

  getMaxDate(day: any) {
    return MJ(this.serverTimeService.getNow()).add(day, 'day').locale('fa').format('jYYYYjMMjDD');
  }

  /** Compact Jalali date N jYears before today. */
  getYearsAgo(years: number): string {
    return MJ(this.serverTimeService.getNow()).subtract(years, 'jYear').locale('fa').format('jYYYYjMMjDD');
  }

  /** Compact Jalali date N jYears after today. */
  getYearsAhead(years: number): string {
    return MJ(this.serverTimeService.getNow()).add(years, 'jYear').locale('fa').format('jYYYYjMMjDD');
  }

  getMonthsAgo(months: number): string {
    return MJ(this.serverTimeService.getNow()).subtract(months, 'jMonth').locale('fa').format('jYYYYjMMjDD');
  }

  getCurrentTime() {
    return MJ(this.serverTimeService.getNow()).locale('fa').format('HHmmss');
  }

  getPassedDefinedPeriod(oneMoreMin: boolean = false, leadMinutes: number = 20) {
    const minutesToAdd = oneMoreMin ? leadMinutes + 1 : leadMinutes;
    return MJ(this.serverTimeService.getNow()).add(minutesToAdd, 'minutes').locale('fa').format('HHmmss');
  }


  translateCronExpression(cron: string): string {
    const [minute, hour, dayOfMonth, month, dayOfWeek] = cron.split(' ');

    // Ensure hour and minute are always two digits (e.g., 09:05 instead of 9:5)
    const formattedHour = hour.padStart(2, '0');
    const formattedMinute = minute.padStart(2, '0');

    if (dayOfMonth === '*' && dayOfWeek === '*') {
      // Daily
      return `هر روز ساعت ${formattedHour}:${formattedMinute}`;
    }

    if (dayOfMonth !== '*' && dayOfWeek === '*') {
      // Monthly
      return `روز ${dayOfMonth} هر ماه، ساعت ${formattedHour}:${formattedMinute}`;
    }

    if (dayOfWeek !== '*') {
      // Weekly
      const daysMap: { [key: string]: string } = {
        '0': 'یکشنبه', '1': 'دوشنبه', '2': 'سه‌شنبه', '3': 'چهارشنبه',
        '4': 'پنج‌شنبه', '5': 'جمعه', '6': 'شنبه'
      };

      const daysTranslated = dayOfWeek.split(',').map(d => daysMap[d] || d).join('، ');

      return `روزهای ${daysTranslated} ساعت ${formattedHour}:${formattedMinute}`;
    }

    return 'فرمت کرون پشتیبانی نمی‌شود';
  }


  shortenText(text: string, length: number = 28): string {
    if (!text) {
      return text;
    }

    const trimmed = String(text).trim();
    if (trimmed.length <= length) {
      return trimmed;
    }

    return `${trimmed.slice(0, length)}…`;
  }

  convertBooleansToString(obj: { [key: string]: any }, reverseMode?: boolean): any {
    if (!reverseMode) {
      return Object.entries(obj).reduce((acc, [key, value]) => {
        acc[key] = typeof value === 'boolean' ? value.toString() : value;
        return acc;
      }, {} as { [key: string]: any });
    }
    return Object.entries(obj).reduce((acc, [key, value]) => {
      if (typeof value === 'string')
        switch (value.toLowerCase()) {
          case 'true':
            acc[key] = true;
            break;
          case 'false':
            acc[key] = false;
            break;

          default:
            acc[key] = value;
            break;
        }
      else {
        acc[key] = value;
      }


      return acc;
    }, {} as { [key: string]: any });
  }

  convertStringNumbersToIntType(obj: any) {
    const result: any = {};

    // Iterate through each key-value pair in the object
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const value = obj[key];

        // Check if the value is a string representation of a number
        if (!isNaN(value) && typeof value === 'string') {
          result[key] = Number(value); // Convert the string to a number and add to the result object
        } else {
          result[key] = value; // If not a string representation of a number, keep the original value
        }
      }
    }

    return result;
  }

  convertToNullableNumber(value: any): number | null {
    return value === 'null' || isNaN(value) || !value ? null : Number(value);
  }

  // 08:00:12 --> [8, 0, 12]
  timeConvertor(time: string): number[] {
    return time.split(":").map(Number)
  }
}
